"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

/**
 * Un filtro compatto che si apre.
 *
 * A riposo è una riga: il nome del filtro, cosa è selezionato, e un pulsantino
 * «cerca». Tre riquadri aperti occupavano mezza pagina prima di far vedere un
 * solo prodotto — e su un catalogo vero, con decine di materiali, ne
 * occuperebbero di più.
 *
 * **È un `<details>`, non uno stato React.** Aprire e chiudere lo fa il
 * browser, quindi funziona anche senza JavaScript: si vede la riga, la si apre,
 * ci sono tutte le opzioni. Il campo di testo è l'unica parte che ha bisogno di
 * JavaScript, e serve solo a nascondere le opzioni che non interessano.
 *
 * **Le opzioni restano link.** Ognuna è un `<a href>` verso la stessa pagina con
 * quel filtro applicato: ogni combinazione ha un URL da mandare a un collega, e
 * i motori di ricerca la percorrono. Una tendina che cambia stato in JavaScript
 * toglierebbe tutte e due le cose.
 *
 * Il pannello galleggia sopra il contenuto invece di spingerlo in basso: aprire
 * un filtro non deve far saltare la griglia dei prodotti sotto le mani.
 */

/* L'indirizzo di ogni opzione arriva già calcolato invece che da una funzione:
   fra un componente del server e uno del client passano dati, non funzioni, e
   comporre l'URL resta compito di chi conosce i filtri attivi. */
export type Opzione = { valore: string; etichetta: string; href: string };

export function FiltroCercabile({
  etichetta,
  opzioni,
  attivo,
  hrefTutti,
  tutti = "tutti",
  segnaposto,
}: {
  etichetta: string;
  opzioni: Opzione[];
  /** Il valore selezionato, se c'è. */
  attivo?: string;
  /** Dove si va togliendo questo filtro. */
  hrefTutti: string;
  /** Come si chiama l'assenza di filtro: «tutti», «ovunque». */
  tutti?: string;
  segnaposto?: string;
}) {
  const [cerca, setCerca] = useState("");

  const visibili = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    if (!q) return opzioni;
    return opzioni.filter((o) => o.etichetta.toLowerCase().includes(q));
  }, [cerca, opzioni]);

  const scelto = opzioni.find((o) => o.valore === attivo);

  return (
    <details className="group relative">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-sm transition hover:border-accent [&::-webkit-details-marker]:hidden">
        <span className="font-medium">{etichetta}</span>
        <span className={scelto ? "text-accent" : "text-muted"}>
          {scelto ? scelto.etichetta : tutti}
        </span>
        <span className="rounded-md border border-line px-1.5 py-0.5 text-xs text-muted transition group-open:border-accent group-open:text-accent">
          cerca
        </span>
      </summary>

      <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded-xl border border-line bg-overlay p-3 shadow-lg">
        {/* Solo se le opzioni sono tante: su cinque voci un campo di ricerca è
            un ostacolo in più fra chi guarda e quello che vuole. */}
        {opzioni.length > 6 && (
          <input
            type="search"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder={segnaposto ?? `Cerca ${etichetta.toLowerCase()}…`}
            aria-label={`Cerca fra ${etichetta.toLowerCase()}`}
            className="mb-2 w-full rounded-lg border border-line bg-surface-strong px-3 py-1.5 text-sm text-on-strong outline-none placeholder:text-on-strong-muted focus:border-accent"
          />
        )}

        {/* La lista scorre invece di allungare il pannello: l'altezza non deve
            dipendere da quanti materiali ci sono a catalogo. */}
        <ul className="max-h-64 space-y-0.5 overflow-y-auto pr-1">
          <li>
            <Link
              href={hrefTutti}
              className={`block rounded-lg px-2 py-1 text-sm transition ${
                scelto
                  ? "text-muted hover:bg-surface-strong hover:text-on-strong"
                  : "bg-accent font-medium text-accent-contrast"
              }`}
            >
              {tutti}
            </Link>
          </li>

          {visibili.map((o) => (
            <li key={o.valore}>
              <Link
                href={o.href}
                className={`block rounded-lg px-2 py-1 text-sm transition ${
                  o.valore === attivo
                    ? "bg-accent font-medium text-accent-contrast"
                    : "hover:bg-surface-strong hover:text-on-strong"
                }`}
              >
                {o.etichetta}
              </Link>
            </li>
          ))}

          {visibili.length === 0 && (
            <li className="px-2 py-1 text-sm text-muted">Nessuna corrispondenza.</li>
          )}
        </ul>
      </div>
    </details>
  );
}
